import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import type { ProductImage } from '@/types/api'
import { Image as ImageIcon, Star, Upload, X } from 'lucide-react'
import React, { useCallback, useRef, useState } from 'react'

interface ProductImagesProps {
  product?: {
    id: string
    images: ProductImage[]
  }
  title?: string
  description?: string
  imageLabel?: string
  embedded?: boolean
  onImageUpload?: (_files: FileList | null) => void
  onDeleteImage?: (_imageId: string) => void
  onSetPrimaryImage?: (_imageId: string) => void
  isUploading?: boolean
  className?: string
}

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB in bytes
const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/png']

export const ProductImages: React.FC<ProductImagesProps> = ({
  product,
  title = 'Product Images',
  description,
  imageLabel = 'Product Images',
  embedded = false,
  onImageUpload,
  onDeleteImage,
  onSetPrimaryImage,
  isUploading = false,
  className,
}) => {
  const { toast } = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragActive, setDragActive] = useState(false)

  const validateFile = useCallback(
    (file: File): boolean => {
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        toast({
          title: 'Error',
          description: 'Only JPEG and PNG files are allowed',
          variant: 'destructive',
        })
        return false
      }

      if (file.size > MAX_FILE_SIZE) {
        toast({
          title: 'Error',
          description: 'File size must be less than 5MB',
          variant: 'destructive',
        })
        return false
      }

      return true
    },
    [toast]
  )

  const handleFileUpload = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return

      const file = files[0]
      if (!validateFile(file)) return

      onImageUpload?.(files)
    },
    [onImageUpload, validateFile]
  )

  const handleDeleteImage = useCallback(
    (imageId: string) => {
      onDeleteImage?.(imageId)
    },
    [onDeleteImage]
  )

  const handleSetPrimary = useCallback(
    (imageId: string) => {
      onSetPrimaryImage?.(imageId)
    },
    [onSetPrimaryImage]
  )

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setDragActive(false)

      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        handleFileUpload(e.dataTransfer.files)
      }
    },
    [handleFileUpload]
  )

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0]) {
        handleFileUpload(e.target.files)
      }
    },
    [handleFileUpload]
  )

  const handleUploadClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }, [])

  const content = (
    <div className="space-y-4">
      {/* Upload Area */}
      <div>
        <Label>Upload New Image</Label>
        <div
          className={`relative mt-2 flex h-32 w-full cursor-pointer items-center justify-center rounded-lg border-2 border-dashed transition-colors ${
            dragActive
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 bg-gray-50 hover:border-gray-400'
          }`}
          onDragEnter={handleDrag}
          onDragOver={handleDrag}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleUploadClick}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png"
            onChange={handleInputChange}
            className="hidden"
          />
          <div className="text-center">
            {isUploading ? (
              <div className="flex flex-col items-center">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
                <p className="mt-2 text-sm text-gray-600">Uploading...</p>
              </div>
            ) : (
              <>
                <Upload className="mx-auto h-12 w-12 text-gray-400" />
                <div className="mt-2">
                  <p className="text-sm text-gray-600">Click to upload or drag and drop</p>
                  <p className="text-xs text-gray-500">JPEG, PNG up to 5MB</p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Existing Images */}
      {product?.images && product.images.length > 0 && (
        <div>
          <Label>
            {imageLabel} ({product.images.length})
          </Label>
          <div className="mt-2 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {product.images.map((image) => (
              <div key={image.id} className="relative group">
                <div className="overflow-hidden rounded-lg border border-gray-200">
                  <img
                    src={image.image_url}
                    alt={image.alt_text || 'Product image'}
                    className="h-32 w-full object-cover"
                  />
                  {image.is_primary && (
                    <Badge className="absolute left-2 top-2 bg-blue-600 text-white text-xs">
                      Primary
                    </Badge>
                  )}
                </div>
                <div className="mt-2 flex justify-between gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  {!image.is_primary && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleSetPrimary(image.id)}
                      className="h-8 px-2 text-xs"
                    >
                      <Star className="h-3 w-3 mr-1" />
                      Set Primary
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDeleteImage(image.id)}
                    className="h-8 px-2 text-xs"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(!product?.images || product.images.length === 0) && (
        <div className="text-center py-8">
          <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-sm text-gray-600">No images uploaded yet</p>
          <p className="text-xs text-gray-500">Upload your first image to get started</p>
        </div>
      )}
    </div>
  )

  if (embedded) {
    return (
      <div className={className}>
        <div className="mb-3">
          <div className="flex items-center gap-2 text-base font-semibold text-foreground">
            <ImageIcon className="h-4 w-4" />
            {title}
          </div>
          {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
        </div>
        {content}
      </div>
    )
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center">
          <ImageIcon className="h-5 w-5 mr-2" />
          {title}
        </CardTitle>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  )
}

export default ProductImages
